import {
    useState,
    useEffect,
    memo,
} from 'react';
import { 
    useNavigate,
    useLocation, 
} from "react-router-dom";
import axios from 'axios';
import { 
    useTheme,
    useMediaQuery,
    Box,
} from '@mui/material';
import { withPather } from 'react-pather';
import RedirectsTopBar from './RedirectsTopBar';
import RedirectHistoryRow from './RedirectHistoryRow';
import RedirectChart from './RedirectChart';
import NoData from './NoData';
import Loader from '../../../Loader';
import { Pagination } from '../../../general';
import { 
    PeriodOptions, 
    periodOptions, 
} from '../../settings/tabs/common';
import { useEffectExceptOnMount } from '../../../../hooks';

const redirectsViewModes = {
    history: 'history',
    chart: 'chart',
}

const redirectsChartTypes = {
    timeline: 'timeline',
    pie: 'pie',
    bar: 'bar',
}

const PREFIX = 'view-conv-';
const REDIRECTS_VIEW_MODE = `${PREFIX}redirects-view-mode`;
const REDIRECTS_CHART_TYPE = `${PREFIX}redirects-chart-type`;

const QueryParam = {
    PERPAGE: 'perPage',
    OFFSET: 'offset',
    STATUS: 'status',
    PERIOD: 'period',
}

const DefaultSettings = {
    perPage: 25,
    offset: 0,
    status: 'all',
    period: 'all',
}

const RedirectDisplay = memo(({ pather, code }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    // Breakpoints
    const matchesDownLg = useMediaQuery(theme.breakpoints.down('lg'));

    // General
    const [error, setError] = useState('');
    const [redirectsLoading, setRedirectsLoading] = useState(false);
    const [redirectsViewMode, setRedirectsViewMode] = useState(redirectsViewModes.history);

    // Redirect History
    const [redirects, setRedirects] = useState([]);
    const [redirectsTotalPages, setRedirectsTotalPages] = useState(0);

    // Redirect History Rows
    const [isExpanded, setExpansion] = useState(true);
    const [hoveredIndex, setHoveredIndex] = useState(null);  // For Redirect Rows
    const [historyPeriods, setHistoryPeriods] = useState([]);
    const [redirectStatuses, setRedirectStatuses] = useState([]);

    // Redirect Chart
    const [redirectsChartType, setRedirectsChartType] = useState(redirectsChartTypes.bar);
    const [chartPeriod, setChartPeriod] = useState(periodOptions[0]);
    const [series, setSeries] = useState([]);
    const [labels, setLabels] = useState([]);
    const [availablePeriodOptions, setAvailablePeriodOptions] = useState([]);

    // Redirect History & Chart
    const [redirectsNumOfResults, setRedirectsNumOfResults] = useState(0);

    // If `code` prop is passed than RedirectsDisplay is for displaying redirects for only one specific conversion,
    //  otherwise (if `code` prop is not passed) RedirectsDisplay is for displaying all user's redirects.
    const isCode = !!code;

    // Query Params Control
    const params = new URLSearchParams(location.search);

    const setQueryParam = (key, val) => {
        if (DefaultSettings[key] === val) {
            params.delete(key);  // Remove the query param
        } else {
            params.set(key, val); // Add or update freshness param
        }
        navigate(
            `${location.pathname}?${params.toString()}`, 
            { replace: true }  // prevents adding an extra entry to history.
        );
    }

    const setPerPage = val => setQueryParam(QueryParam.PERPAGE, val);
    const setOffset = val => setQueryParam(QueryParam.OFFSET, val);
    const setStatus = val => setQueryParam(QueryParam.STATUS, val);
    const setPeriod = val => setQueryParam(QueryParam.PERIOD, val);

    const setMethods = {
        setPerPage,
        setOffset,
        setStatus,
        setPeriod
    }

    const getQueryParam = key => params.get(key) || DefaultSettings[key];  // Fallback if not set

    const getPerPage = () => getQueryParam(QueryParam.PERPAGE);
    const getOffset = () => getQueryParam(QueryParam.OFFSET);
    const getStatus = () => getQueryParam(QueryParam.STATUS);
    const getPeriod = () => getQueryParam(QueryParam.PERIOD);

    const settings = { 
        perPage: getPerPage(),
        offset: getOffset(),
        status: getStatus(),
        period: getPeriod(),
    };

    const {
        perPage,
        offset,
        status,
        period,
    } = settings;
    

    // Data Fetching Methods
    const fetchRedirects = (success = null) => {

        setRedirectsLoading(true);

        const url = isCode ?
            pather.reverse(
                pather.back.Conversion.redirects, 
                { code },
            ) :
            pather.back.redirectsTotal;
        
        axios.get(
            url,
            {
                params: {
                    status,
                    period,
                    offset,
                    perPage,
                }, 
            }   
        )
        .then(response => {
            const data = response?.data;
            setRedirects(data?.redirects);
            setRedirectsTotalPages(data?.total_pages);
            setRedirectsNumOfResults(data?.number_of_results);
            setHistoryPeriods(data?.history_periods || []);
            setRedirectStatuses(data?.redirect_statuses || []);
            success && success();
        })
        .catch(error => {
            console.log(error);
            const status = error?.data?.status;
            if(status) setError(status);
            else {
                if (error?.response?.status === 404) setError('Resource is not found!');
                else setError('Something went wrong!');
            }
        })
        .finally(() => {
            setRedirectsLoading(false);
        });
    }

    const fetchChartData = () => {

        setRedirectsLoading(true);

        const url = isCode ?
            pather.reverse(
                pather.back.Conversion.redirectsChart, 
                { code },
            ) :
            pather.back.redirectsTotalChart;

        axios.get(
            url,
            {
                params: {
                    chartType: redirectsChartType,
                    chartPeriod: chartPeriod.value,
                }, 
            }   
        )
        .then(response => {
            const data = response?.data;
            setLabels(data?.labels);
            setSeries(data?.series);
            setRedirectsNumOfResults(data?.number_of_results);
            setAvailablePeriodOptions(data?.period_options);
        })
        .catch(error => {
            console.log(error);
            const status = error?.data?.status;
            if(status) setError(status);
            else setError('Something went wrong!');
        })
        .finally(() => {
            setRedirectsLoading(false);
        });
    }


    // Hooks
    useEffect(() => {
        fetchRedirects();
        const storedRedirectsViewMode = localStorage.getItem(REDIRECTS_VIEW_MODE);
        if (storedRedirectsViewMode) setRedirectsViewMode(storedRedirectsViewMode);
        const storedRedirectsChartType = localStorage.getItem(REDIRECTS_CHART_TYPE);
        if (storedRedirectsChartType) setRedirectsChartType(storedRedirectsChartType);
    }, []);

    useEffectExceptOnMount(() => {
        if (redirectsViewMode === redirectsViewModes.history) 
            fetchRedirects();
    }, [offset]);

    useEffectExceptOnMount(() => {
        if (redirectsViewMode === redirectsViewModes.history) 
            fetchRedirectsWithOffsetReset(); 
    }, [perPage, status, period]);

    useEffectExceptOnMount(() => {
        clean();
        redirectsViewMode === redirectsViewModes.history ?
            fetchRedirectsWithOffsetReset() : 
            fetchChartData();
    }, [redirectsViewMode]);

    useEffectExceptOnMount(() => {
        if (redirectsViewMode === redirectsViewModes.chart) {
            clean(); 
            fetchChartData();
        }
    }, [redirectsChartType, chartPeriod])


    // Functions
    const resetSettings = () => navigate(location.pathname, { replace: true });

    const clean = () => {
        setLabels([]);
        setSeries([]);
        setRedirectsNumOfResults(0);
        setAvailablePeriodOptions([]);
    }

    const reload = () => {
        clean();
        redirectsViewMode === redirectsViewModes.history ?
        fetchRedirectsWithOffsetReset() :
        fetchChartData();
    }

    const fetchRedirectsWithOffsetReset = () => {
        fetchRedirects(() => {
            setOffset(0);
        });
    }

    const setRedirectsViewModeLS = ({ target: { value } }) => {
        setRedirectsViewMode(value);
        localStorage.setItem(REDIRECTS_VIEW_MODE, value);
        if (value === redirectsViewModes.chart) resetSettings(); 
    }

    const setRedirectsChartTypeLS = value => {
        setRedirectsChartType(value);
        localStorage.setItem(REDIRECTS_CHART_TYPE, value);
    }

    const handleOffsetChange = (event, value) => setOffset(value - 1);


    // Prevent `Redirect Chart` on small screens
    if (matchesDownLg && redirectsViewMode !== redirectsViewModes.history) {
        setRedirectsViewMode(redirectsViewModes.history);
    }

    const boxMinHeight = isCode ? '250px' : '550px';

    return (
        <>
            {
                redirectsLoading ?
                <Loader boxMinHeight={boxMinHeight}/> :
                <>
                    {   
                        error ? 
                        <Box sx={{
                            display: 'flex', width: '100%', justifyContent: 'center', 
                            alignItems: 'center', minHeight: '320px'
                        }}>
                            <h3>{error} &#128546;</h3>
                        </Box> :
                        <>
                            <Box 
                                sx={{
                                    mt: 2, 
                                    width: '100%', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    flexDirection: 'column', 
                                    gap: 1,
                                }}
                            >
                                <RedirectsTopBar
                                    redirectsNumOfResults={redirectsNumOfResults}
                                    reload={reload}
                                    resetSettings={resetSettings}
                                    redirectsViewMode={redirectsViewMode}
                                    setRedirectsViewMode={setRedirectsViewModeLS}
                                    redirectsChartType={redirectsChartType}
                                    setRedirectsChartType={setRedirectsChartTypeLS}
                                    matchesRedirectsHistoryOnly={matchesDownLg}
                                    isExpanded={isExpanded}
                                    setExpansion={setExpansion}
                                    settings={settings}
                                    setMethods={setMethods}
                                    historyPeriods={historyPeriods}
                                    redirectStatuses={redirectStatuses}
                                />
                                {   
                                    redirectsNumOfResults > 0 ?
                                    <Box>
                                        {
                                            redirectsViewMode === redirectsViewModes.history || matchesDownLg ?
                                            <>
                                                {
                                                    redirects.map((redirect, index) => (
                                                        <RedirectHistoryRow 
                                                            key={index}
                                                            redirect={redirect} 
                                                            index={index}
                                                            isExpanded={isExpanded}
                                                            hoveredIndex={hoveredIndex}
                                                            setHoveredIndex={setHoveredIndex}
                                                        />
                                                    ))
                                                }
                                            </> :
                                            <Box 
                                                sx={{
                                                    width: '100%', 
                                                    display: 'flex', 
                                                    flexDirection: 'column', 
                                                    alignItems: 'center',
                                                    gap: redirectsChartType === redirectsChartTypes.pie && 2,
                                                }}
                                            >
                                                <RedirectChart 
                                                    labels={labels}
                                                    series={series}
                                                    redirectsChartType={redirectsChartType}
                                                    redirectsNumOfResults={redirectsNumOfResults}
                                                />
                                            </Box>
                                        }
                                    </Box> :
                                    <NoData 
                                        text='No redirects' 
                                        minHeight={boxMinHeight}
                                    />
                                }
                                {
                                    redirectsViewMode === redirectsViewModes.chart && availablePeriodOptions.length > 0 &&
                                    <PeriodOptions 
                                        period={chartPeriod} 
                                        setPeriod={setChartPeriod} 
                                        availableOptions={availablePeriodOptions}
                                        extended={true}
                                    />
                                }
                            </Box>
                            {   
                                redirectsViewMode === redirectsViewModes.history && redirectsTotalPages > 1 && 
                                <Box sx={{width: '100%', mt: 3, display: 'flex', justifyContent: 'center'}}>
                                    <Pagination 
                                        offset={offset} 
                                        totalPages={redirectsTotalPages} 
                                        handleOffsetChange={handleOffsetChange}
                                    />
                                </Box>
                            }
                        </>
                    }
                </>
            }
        </>
    );
});

export {
    redirectsViewModes,
    redirectsChartTypes,
    QueryParam,
    DefaultSettings,
}

export default withPather()(RedirectDisplay);